import { useMemo, useState, useEffect, useCallback } from "react";
import type { ReactNode } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { Box } from "ui/box";
import { TextField } from "ui/text-field";
import { Select } from "ui/select";
import SelectedItems from "components/partials/selected-items/selected-items";
import SelectedItem from "components/partials/selected-items/selected-item/selected-item";
import { Checkbox } from "ui/checkbox";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";

import { getTruncatedTouchpointsName } from "containers/admin/clients/campaign/campaign-creative-page/components/creactive-table/creative-table.helper";

import { TouchpointAttributes } from "hooks/api/use-touchpoints";
import { CampaignFolderDTO } from "generated/schemas";
import { useTouchpoints } from "hooks/api/use-touchpoints";
import { useFolders } from "hooks/api/use-folders";
import { LoadingSpinner } from "components/partials/loading-screen/loading-screen";

import { colors } from "design-tokens";

type CreateFolderFormValues = {
  name: string;
  addedTouchpoints?: TouchpointAttributes[];
};

type TouchpointSelectOption = {
  label: ReactNode;
  value: string; // touchpoint id
  touchpoint: TouchpointAttributes;
  shortValueToDisplay: string;
};

const CreateFolderForm = ({
  setIsSubmitDisabled,
  touchpointsNotIncluded = false,
  currentFolderName,
}: {
  setIsSubmitDisabled: (isDisabled: boolean) => void;
  touchpointsNotIncluded?: boolean;
  currentFolderName?: string;
}) => {
  const {
    control,
    formState: { errors },
    watch,
    setError,
    clearErrors,
    setValue,
  } = useFormContext<CreateFolderFormValues>();

  const [includeTouchpoints, setIncludeTouchpoints] = useState(false);

  const currentCampaign = useCurrentCampaign();
  const currentClient = useCurrentClient();

  const { data: foldersData } = useFolders();
  const allCampaignFolders = useMemo(() => foldersData?.content ?? [], [foldersData]);

  const { data, isLoading } = useTouchpoints(
    { sort: ["name,asc"] },
    {
      enabled: includeTouchpoints && !touchpointsNotIncluded,
    },
  );

  const touchpoints = useMemo(() => {
    if (!data) return [];
    return data.items.filter((tp) => !tp.folderId);
  }, [data]);

  const selectedTouchpoints = watch("addedTouchpoints") ?? [];

  useEffect(() => {
    if (currentFolderName) {
      setValue("name", currentFolderName);
    }
  }, [currentFolderName, setValue]);

  useEffect(() => {
    setValue("addedTouchpoints", []);
  }, [currentCampaign, currentClient.id, setValue]);

  const touchpointsOptions = useMemo<TouchpointSelectOption[]>(
    () =>
      touchpoints.map((touchpoint) => ({
        label: (
          <SelectedItem
            name={touchpoint.name}
            latestVersion={touchpoint.latestVersion}
            type={touchpoint.type}
            showStatus={false}
          />
        ),
        value: touchpoint.id,
        touchpoint,
        shortValueToDisplay: getTruncatedTouchpointsName(touchpoint.name),
      })),
    [touchpoints],
  );

  const nameValue = watch("name") || "";

  const onIncludeTouchpointsChange = () => {
    const next = !includeTouchpoints;
    setIncludeTouchpoints(next);
    setValue("addedTouchpoints", []);

    const isDisabled = !nameValue.length;
    setIsSubmitDisabled(isDisabled);
  };

  const checkFolderName = useCallback(
    (name: string) => {
      if (currentFolderName && name.toLowerCase() === currentFolderName.toLowerCase()) {
        return false;
      }

      return allCampaignFolders.some(
        (folder: CampaignFolderDTO) => folder.name.toLowerCase() === name.toLowerCase(),
      );
    },
    [allCampaignFolders, currentFolderName],
  );

  // Validation for folder name
  useEffect(() => {
    const trimmedName = nameValue.trim();
    if (!trimmedName) {
      if (errors.name) clearErrors("name");
      return;
    }

    const isDuplicate = checkFolderName(trimmedName);
    if (isDuplicate && !errors.name) {
      setError("name", {
        type: "manual",
        message: "Folder with this name already exists. Please enter another name.",
      });
    } else if (!isDuplicate && errors.name) {
      clearErrors("name");
    }
  }, [nameValue, checkFolderName, setError, clearErrors, errors.name]);

  useEffect(() => {
    const hasNameError = !!errors?.name?.message;
    const isNoName = !nameValue.length;
    const shouldDisableSubmit =
      hasNameError || isNoName || (includeTouchpoints && !selectedTouchpoints.length);

    setIsSubmitDisabled(shouldDisableSubmit);
  }, [
    errors?.name?.message,
    includeTouchpoints,
    selectedTouchpoints,
    nameValue,
    setIsSubmitDisabled,
  ]);

  const nameErrorMessage = errors?.name?.message ? String(errors.name.message) : undefined;

  return (
    <>
      <Controller
        name="name"
        control={control}
        render={({ field }) => (
          <TextField
            label="Folder name"
            required
            errMessage={nameErrorMessage}
            {...field}
            onChange={(e) => {
              field.onChange(e);
            }}
          />
        )}
      />
      {!touchpointsNotIncluded && (
        <>
          <div className="flex flex-col">
            <Checkbox
              size="sm"
              label="Include creatives to new folder"
              checked={includeTouchpoints}
              onChange={onIncludeTouchpointsChange}
            />
          </div>
          {includeTouchpoints && (
            <div className="flex flex-col min-h-60 gap-3">
              {isLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    height: "200px",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                  <LoadingSpinner size="lg" color={colors.aqua[500]} />
                </Box>
              ) : (
                <>
                  <Controller
                    name="addedTouchpoints"
                    control={control}
                    rules={{ required: includeTouchpoints }}
                    render={({ field }) => {
                      const currentValue: TouchpointAttributes[] = field.value ?? [];
                      const selectedOptions = touchpointsOptions.filter((opt) =>
                        currentValue.some((tp) => tp.id === opt.value),
                      );

                      return (
                        <div className="flex flex-col gap-1">
                          <Select
                            required={includeTouchpoints}
                            label="Creatives"
                            ariaLabel="Add creatives to folder"
                            selectName={`touchpoints-select-${field.name}`}
                            options={touchpointsOptions.length ? touchpointsOptions : []}
                            multiple
                            value={selectedOptions}
                            placeholder={
                              touchpointsOptions.length
                                ? undefined
                                : "No creatives in the ‘Not in a Folder’ section"
                            }
                            disabled={!touchpointsOptions.length}
                            onChange={(selected) => field.onChange(selected.map((o) => o.touchpoint))}
                          />
                        </div>
                      );
                    }}
                  />

                  <SelectedItems selectedList={selectedTouchpoints} showStatus={false} />
                </>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
};

export default CreateFolderForm;
